import React, { useEffect, useRef, useState } from "react";

import { Avatar, CardHeader } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import { formatarNomeOuTelefone } from "../../utils/phone";
import openSocket from "../../services/socket-io";
import api from "../../services/api";
import {
  APARELHOS,
  ORIGENS,
  rotuloOrigem,
  quandoConsultou,
  textoPresenca,
  resumoPedidoLeilao,
} from "./infoCliente";

// Cabecalho do atendimento, em tres linhas curtas:
//   1) nome
//   2) presenca (online / visto por ultimo / digitando) · atribuido
//   3) selos pequenos (alerta, aparelho, regiao, origem, conta, atendimentos)
// O recado do perfil e os detalhes ficam na barra lateral (Dados do contato).

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "8px 12px",
    minWidth: 0,
    "& .MuiCardHeader-content": { minWidth: 0, overflow: "hidden" },
    "& .MuiCardHeader-avatar": { marginRight: 12 },
  },
  avatar: { width: 44, height: 44 },
  nome: {
    display: "block",
    fontWeight: 600,
    fontSize: "0.95rem",
    lineHeight: 1.25,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  linha: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    minWidth: 0,
    overflow: "hidden",
    whiteSpace: "nowrap",
    lineHeight: "18px",
  },
  texto: {
    fontSize: "0.74rem",
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  textoCorta: {
    fontSize: "0.74rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  },
  online: { color: "#1E9855", fontWeight: 600 },
  bolinha: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    flexShrink: 0,
  },
  bolinhaOn: { backgroundColor: "#1E9855" },
  bolinhaOff: { backgroundColor: theme.palette.type === "dark" ? "#6b7280" : "#b8c0c8" },
  digitando: { color: "#1E9855", fontWeight: 600, fontStyle: "italic" },
  selos: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: 3,
    minWidth: 0,
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  selo: {
    flexShrink: 0,
    fontSize: "0.6rem",
    fontWeight: 700,
    lineHeight: "15px",
    padding: "0 6px",
    borderRadius: 7,
    letterSpacing: 0.2,
  },
  iphone: { color: "#1A73E8", backgroundColor: "rgba(26, 115, 232, 0.12)" },
  android: { color: "#D93025", backgroundColor: "rgba(217, 48, 37, 0.12)" },
  neutro: {
    color: theme.palette.text.secondary,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
  },
  alerta: { color: "#FFFFFF", backgroundColor: "#D93025" },
}));

const EXPIRA_DIGITANDO_MS = 8000;

const soDigitos = (v) => String(v || "").replace(/\D/g, "");

const TicketInfo = ({ contact, ticket, onClick }) => {
  const classes = useStyles();

  const [presenca, setPresenca] = useState(null); // { status, lastSeen }
  const [info, setInfo] = useState(null);
  const [agora, setAgora] = useState(Date.now());
  const timerRef = useRef(null);

  const numeroDoContato = soDigitos(contact?.number);
  const lidDoContato = soDigitos(String(contact?.lid || "").split("@")[0]);

  // Presenca ao vivo (o WhatsApp so manda enquanto estamos inscritos).
  useEffect(() => {
    if (!ticket?.id) return undefined;
    let ativo = true;
    setPresenca(null);

    const assinar = () => {
      api.post(`/tickets/${ticket.id}/presence`).catch(() => {});
    };
    assinar();
    const intervalo = setInterval(assinar, 45000);

    const socket = openSocket();
    const aoReceberPresenca = (dados) => {
      if (!ativo) return;
      const numero = soDigitos(dados?.number);
      if (!numero) return;

      // O WhatsApp pode avisar pelo numero OU pelo LID do cliente.
      const combina =
        (lidDoContato && numero === lidDoContato) ||
        (numeroDoContato &&
          (numero.endsWith(numeroDoContato.slice(-8)) ||
            numeroDoContato.endsWith(numero.slice(-8))));
      if (!combina) return;

      setPresenca((anterior) => ({
        status: dados.status,
        lastSeen: dados.lastSeen || anterior?.lastSeen || null,
      }));

      if (timerRef.current) clearTimeout(timerRef.current);
      if (dados.status === "composing" || dados.status === "recording") {
        timerRef.current = setTimeout(() => {
          setPresenca((p) => (p ? { ...p, status: "available" } : p));
        }, EXPIRA_DIGITANDO_MS);
      }
    };
    socket.on("presence", aoReceberPresenca);

    return () => {
      ativo = false;
      clearInterval(intervalo);
      if (timerRef.current) clearTimeout(timerRef.current);
      socket.off("presence", aoReceberPresenca);
    };
  }, [ticket?.id, numeroDoContato, lidDoContato]);

  // Selos do cliente: ao abrir, a cada mensagem/tique do ticket e a cada 1 min.
  useEffect(() => {
    if (!ticket?.id) return undefined;
    let ativo = true;
    let espera = null;

    const carregar = () => {
      api
        .get(`/tickets/${ticket.id}/info-cliente`)
        .then(({ data }) => {
          if (!ativo) return;
          setInfo(data);
          // Presenca guardada no servidor: mostra ja ao abrir (se recente).
          const p = data?.presenca;
          if (p && Date.now() - p.em < 30 * 60 * 1000) {
            setPresenca((atual) => atual || { status: p.status, lastSeen: p.lastSeen });
          }
        })
        .catch(() => {});
    };

    setInfo(null);
    carregar();
    const intervalo = setInterval(carregar, 60000);

    const socket = openSocket();
    const aoMudarMensagem = (data) => {
      if (String(data?.message?.ticketId) !== String(ticket.id)) return;
      clearTimeout(espera);
      espera = setTimeout(carregar, 1500);
    };
    socket.on("appMessage", aoMudarMensagem);

    return () => {
      ativo = false;
      clearInterval(intervalo);
      clearTimeout(espera);
      socket.off("appMessage", aoMudarMensagem);
    };
  }, [ticket?.id]);

  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  // ---- linha 2 -------------------------------------------------------------
  const itensLinha2 = [];

  const pres = textoPresenca(presenca, agora);
  if (pres) {
    if (pres.tipo === "digitando") {
      itensLinha2.push(
        <span key="pres" className={`${classes.texto} ${classes.digitando}`}>
          {pres.texto}
        </span>
      );
    } else {
      itensLinha2.push(
        <span key="pres" className={classes.linha} style={{ gap: 5, flexShrink: 0 }}>
          <span
            className={`${classes.bolinha} ${
              pres.tipo === "online" ? classes.bolinhaOn : classes.bolinhaOff
            }`}
          />
          <span className={`${classes.texto} ${pres.tipo === "online" ? classes.online : ""}`}>
            {pres.texto}
          </span>
        </span>
      );
    }
  }

  if (ticket?.user) {
    itensLinha2.push(
      <span
        key="atrib"
        className={classes.textoCorta}
        title={ticket.atribuidoPor ? `Atribuído por ${ticket.atribuidoPor}` : undefined}
      >
        {`${i18n.t("messagesList.header.assignedTo")} ${ticket.user.name}`}
        {ticket.atribuidoPor ? ` · por ${ticket.atribuidoPor}` : ""}
      </span>
    );
  }

  const linha2 = [];
  itensLinha2.forEach((item, i) => {
    if (i > 0) {
      linha2.push(
        <span key={`sep-${i}`} className={classes.texto}>
          ·
        </span>
      );
    }
    linha2.push(item);
  });

  // ---- linha 3: selos ------------------------------------------------------
  const selos = [];
  if (info?.bloqueado) {
    selos.push(
      <span
        key="bloqueado"
        className={`${classes.selo} ${classes.alerta}`}
        title="O cliente bloqueou este número no WhatsApp. As mensagens enviadas não chegam nele."
      >
        🚫 Bloqueado
      </span>
    );
  } else if (info?.naoEstaRecebendo) {
    selos.push(
      <span
        key="alerta"
        className={`${classes.selo} ${classes.alerta}`}
        title="As últimas mensagens não foram entregues há mais de 3 minutos. Tente ligar ou mandar SMS."
      >
        Não está recebendo
      </span>
    );
  }
  if (APARELHOS[info?.aparelho]) {
    selos.push(
      <span
        key="aparelho"
        className={`${classes.selo} ${classes[info.aparelho]}`}
        title={APARELHOS[info.aparelho].dica}
      >
        {APARELHOS[info.aparelho].rotulo}
      </span>
    );
  }
  if (info?.uf) {
    selos.push(
      <span key="uf" className={`${classes.selo} ${classes.neutro}`} title={`Região pelo DDD ${info.ddd}`}>
        {`${info.uf} · ${info.ddd}`}
      </span>
    );
  }
  if (info) {
    selos.push(
      <span
        key="origem"
        className={`${classes.selo} ${classes.neutro}`}
        title={
          ORIGENS[info.origem]
            ? "Veio pelo link do SMS (a primeira mensagem é o texto do SMS)"
            : "Chamou direto (a primeira mensagem não é o texto do SMS)"
        }
      >
        {rotuloOrigem(info)}
      </span>
    );
  }
  if (resumoPedidoLeilao(info?.leilao)) {
    selos.push(
      <span
        key="leilao"
        className={`${classes.selo} ${classes.neutro}`}
        title="Valor e parcelas que o cliente solicitou (Leilão). Detalhes na aba Leilão em Dados do contato."
      >
        {resumoPedidoLeilao(info.leilao)}
      </span>
    );
  }
  if (info?.business) {
    selos.push(
      <span key="biz" className={`${classes.selo} ${classes.neutro}`} title="O cliente usa WhatsApp Business">
        Business
      </span>
    );
  }
  if (info?.vistoDesligado) {
    selos.push(
      <span
        key="visto"
        className={`${classes.selo} ${classes.neutro}`}
        title="O cliente recebe e responde, mas a confirmação de leitura (tique azul) está desligada"
      >
        Visto desligado
      </span>
    );
  }
  if (info?.atendimentos > 1) {
    selos.push(
      <span key="atend" className={`${classes.selo} ${classes.neutro}`} title="Quantas vezes este cliente já foi atendido">
        {`${info.atendimentos}º atendimento`}
      </span>
    );
  }
  if (info?.ultimaConsultaEm) {
    selos.push(
      <span
        key="consulta"
        className={`${classes.selo} ${classes.neutro}`}
        title={`Última consulta nos bancos: ${new Date(info.ultimaConsultaEm).toLocaleString("pt-BR")}`}
      >
        {quandoConsultou(info.ultimaConsultaEm, agora)}
      </span>
    );
  }

  return (
    <CardHeader
      onClick={onClick}
      className={classes.root}
      style={{ cursor: "pointer" }}
      avatar={<Avatar src={contact?.profilePicUrl} alt="contact_image" className={classes.avatar} />}
      disableTypography
      title={
        <span className={classes.nome} title={formatarNomeOuTelefone(contact?.name, contact?.number)}>
          {formatarNomeOuTelefone(contact?.name, contact?.number)}
        </span>
      }
      subheader={
        <div>
          {linha2.length > 0 && <div className={classes.linha}>{linha2}</div>}
          {selos.length > 0 && <div className={classes.selos}>{selos}</div>}
        </div>
      }
    />
  );
};

export default TicketInfo;
